import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import DiscreteTabs from 'components/DesignSystem/Tabs/DiscreteTabs';
import UnderlineTabs, { UnderlineTabItem } from 'components/Layout/MobileShell/UnderlineTabs';
import StatusDot from 'components/Relatorios/shared/Aprovacao/StatusDot';
import SecaoTabs from 'components/Relatorios/shared/Aprovacao/SecaoTabs';
import RevisaoBar from 'components/Relatorios/shared/Aprovacao/RevisaoBar';
import SecaoAprovacaoCard from 'components/Relatorios/shared/Aprovacao/SecaoAprovacaoCard';
import PublicacaoGate from 'components/Relatorios/shared/Aprovacao/PublicacaoGate';
import RelatorioAprovacaoConfigContext from 'hooks/Relatorios/Aprovacao/RelatorioAprovacaoConfigContext';
import usePainelMobile from 'hooks/Relatorios/usePainelMobile';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import useSecoesDoInforme from 'hooks/Relatorios/Aprovacao/useSecoesDoInforme';
import useIsMobile from 'utils/hooks/useIsMobile';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { slugDaRota } from 'helper/Relatorios/Aprovacao';
import { RelatorioInforme } from 'types/Relatorios/Aprovacao';

export interface RelatorioSecoesViewProps {
  informe: RelatorioInforme;
}

/**
 * Aba de um informe: sub-abas + Outlet para a seção ativa. Fora do `modoRevisao`
 * mantém o `DiscreteTabs` intocado (DOM idêntico ao de antes do fluxo de aprovação:
 * div + tabs + hr + Outlet, sem wrappers); em `modoRevisao` troca para `SecaoTabs`
 * (bolinhas de status + link "Aprovação"), acrescenta a `RevisaoBar` e, quando a rota
 * ativa é uma seção (não `/aprovacao`), o `SecaoAprovacaoCard`. O conteúdo passa sempre
 * pelo `PublicacaoGate` (fail-closed para cliente). A config vem de `getAprovacaoConfig`
 * e é entregue aos filhos por contexto (só config, sem estado). As abas usam
 * `useSecoesDoInforme` (ordem e título do `GET options` do DESSEM, com fallback no config).
 *
 * No mobile (<768px) as seções viram o N2 (sublinhado, `UnderlineTabs`) no lugar de
 * `DiscreteTabs`/`SecaoTabs`/`<hr>`; em `modoRevisao` cada uma ganha o `StatusDot` e a aba
 * "Aprovação" fecha a fila. O "Consulta atual" (painel de controle, vindo do layout por
 * `<Outlet context>`) fica logo abaixo do N2, antes da RevisaoBar. O resto (RevisaoBar,
 * SecaoAprovacaoCard, gate, Outlet) é igual.
 */
export default function RelatorioSecoesView({ informe }: RelatorioSecoesViewProps) {
  const config = getAprovacaoConfig(informe);
  const location = useLocation();
  const { modoRevisao, statusPorSlug } = useRelatorioAprovacao(config);
  const isMobile = useIsMobile();
  const painelMobile = usePainelMobile();
  // Abas: ordem/título do BE (DESSEM) com fallback no config. A rota ativa e a aprovação seguem
  // em `config.secoes`, porque as rotas são do FE.
  const secoes = useSecoesDoInforme(config);
  const slugAtivo = slugDaRota(location.pathname, config.secoes);

  let abas: React.ReactNode;
  if (isMobile) {
    const items: UnderlineTabItem[] = secoes.map(({ slug, label }) => {
      const secao = statusPorSlug?.[slug];
      return {
        path: `${config.base}/${slug}`,
        label,
        trailing: modoRevisao && secao ? <StatusDot status={secao.status} /> : undefined,
      };
    });
    if (modoRevisao) {
      items.push({ path: `${config.base}/aprovacao`, label: 'Aprovação', dividerBefore: true });
    }
    abas = <UnderlineTabs items={items} ariaLabel="Seções do informe" />;
  } else if (modoRevisao) {
    abas = <SecaoTabs />;
  } else {
    abas = (
      <DiscreteTabs type="underline" activeKey={location.pathname}>
        {secoes.map(({ slug, label }) => (
          <DiscreteTabs.Tab key={slug} path={`${config.base}/${slug}`} title={label} />
        ))}
      </DiscreteTabs>
    );
  }

  return (
    <RelatorioAprovacaoConfigContext.Provider value={config}>
      <div>
        {abas}
        {!isMobile && <hr className="m-1" />}
        {isMobile && painelMobile}
        {modoRevisao && <RevisaoBar />}
        {modoRevisao && slugAtivo && <SecaoAprovacaoCard slug={slugAtivo} />}
        <PublicacaoGate>
          <Outlet />
        </PublicacaoGate>
      </div>
    </RelatorioAprovacaoConfigContext.Provider>
  );
}
